import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { api } from "../api/client";

const CACHE_KEY = "pme_feature_flags";

function readCache(): Record<string, boolean> | null {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (raw) return JSON.parse(raw) as Record<string, boolean>;
  } catch { /* ignore */ }
  return null;
}

function writeCache(flags: Record<string, boolean>): void {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(flags));
  } catch { /* ignore */ }
}

interface FeaturesContextValue {
  flags: Record<string, boolean>;
  loading: boolean;
  isLoaded: boolean;
  isEnabled: (key: string) => boolean;
  refresh: () => Promise<void>;
}

const FeaturesContext = createContext<FeaturesContextValue | undefined>(
  undefined,
);

export function FeaturesProvider({ children }: { children: ReactNode }) {
  const cached = readCache();
  const [flags, setFlags] = useState<Record<string, boolean>>(cached ?? {});
  const [loaded, setLoaded] = useState(!!cached);

  const refresh = useCallback(async () => {
    try {
      const data = await api.get<Record<string, boolean>>(
        "/api/reviews/features",
      );
      setFlags(data);
      writeCache(data);
    } catch {
      console.error("Failed to load feature flags");
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const isEnabled = useCallback(
    (key: string): boolean => {
      if (!loaded) return true;
      return !!flags[key];
    },
    [flags, loaded],
  );

  const value = useMemo<FeaturesContextValue>(
    () => ({ flags, loading: !loaded, isLoaded: loaded, isEnabled, refresh }),
    [flags, loaded, isEnabled, refresh],
  );

  return (
    <FeaturesContext.Provider value={value}>
      {children}
    </FeaturesContext.Provider>
  );
}

export function useFeatures(): FeaturesContextValue {
  const ctx = useContext(FeaturesContext);
  if (!ctx) {
    throw new Error("useFeatures must be used within <FeaturesProvider>");
  }
  return ctx;
}
